<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  CollectionGalleryType,
  CollectionGalleryPayloadType,
} from "@/common/types/collection-gallery";

import { baseInput, baseSelect } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: CollectionGalleryType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  status: "ACTIVE",
  name: "",
  slug: "",
};

const payload = ref<CollectionGalleryPayloadType>({ ...DEFAULT_DATA });

const optionsStatus = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, slug, status } = props.data;

  payload.value = {
    ...payload.value,
    name,
    slug,
    status,
  };
}

function maskCode(data: string) {
  return data?.replaceAll(" ", "-")?.toLowerCase();
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-manufacturer">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('common.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("common.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.slug"
      :value="payload.slug"
      :placeholder="t('common.placeholders.slug')"
      :mask="maskCode"
      @update:modelValue="debouceUpdate('slug')"
    >
      <template #label> {{ t("common.labels.slug") }}</template>
    </base-input>

    <base-select
      v-model="payload.status"
      :value="payload.status"
      :options="optionsStatus"
      :is-empty="false"
      @change="debouceUpdate('status')"
    >
      <template #label> {{ t("common.labels.status") }}</template>
    </base-select>
  </section>
</template>

<style lang="scss" scoped>
.form-manufacturer {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
